import * as React from "react";

import {
  Box,
  BoxProps,
  Card,
  Collapse,
  CollapsePanel,
  Link,
  Section,
  Stack,
  Text,
} from "@vinomofo/components";
import { DEVICE, MARKET_AUDIENCE } from "../../utils/constants";
import styled, { css } from "styled-components";
import { useEffect, useState } from "react";

import { CartDropdown } from "../cart-dropdown";
import { HamburgerToggle } from "../hamburger-toggle";
import { HeaderAuth } from "./HeaderAuth";
import { HeaderCategoryNav } from "./HeaderCategoryNav";
import { HeaderLogo } from "./HeaderLogo";
import { HeaderSearch } from "./HeaderSearch";
import { SiteToggle } from "./HeaderWide";
import isBrowser from "../../utils/is-browser";
import { navigate } from "gatsby";
import { storefrontNavigationItemProps } from "../../types/vino-data-types";
import { useLocation } from "@reach/router";
import { useResponsiveValue } from "../../hooks/use-match-media";
import LoginSignup from "./LoginSignup";
import {
  isWhiskymofoDealsPage,
  isWhiskymofoSignupPage,
} from "../../utils/is-page";

const DropDownOverlayStyleOpen = () => css`
  margin: 0;
  padding: 0;
  z-index: 14;
  position: fixed;
  opacity: 1;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background-color: rgba(0, 0, 0, 1);
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-align-items: flex-start;
  -webkit-box-align: flex-start;
  -ms-flex-align: flex-start;
  align-items: flex-start;
  -webkit-transition: opacity 200ms linear;
  transition: opacity 200ms linear;
`;

const DropDownOverlay = styled.div<{ open?: boolean }>`
  ${(props) =>
    props.open ? DropDownOverlayStyleOpen() : "opacity: 0; z-index: -1;"};
`;

const NavigationLink = styled(Box)<{ $isActive?: boolean }>`
  cursor: pointer;
  padding: 14px;
  display: flex;
  width: 100%;
  position: relative;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.almostWhite};
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  background-color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.mofoGreen : theme.colors.text.heading};

  &:before {
    position: absolute;
    content: "";
    width: 4px;
    background-color: ${({ theme, $isActive }) =>
      $isActive ? theme.colors.mofoGreen : "transparent"};
    top: -1px;
    bottom: 0;
    left: 0;
  }

  &:first-child {
    border-top: none;

    &:before {
      top: 0;
    }
  }

  &:hover {
    color: ${({ theme }) => theme.colors.white};
  }

  @media ${({ theme }) => theme.mq.min.mobile} {
    padding: 10px;
  }
`;

const WHISKYMOFO_ENABLED = process.env.GATSBY_ENABLE_WHISKYMOFO === "true";
const NavigationItem = ({
  icon,
  iconColor = "brand",
  label,
  slug,
  isActive = false,
  onClick,
  ...rest
}: NavigationItemProps): JSX.Element => {
  return (
    <React.Fragment>
      <NavigationLink
        as={Link}
        to={slug}
        padding="20px 20px 20px 24px"
        onClick={onClick}
        $isActive={isActive}
        cursor=""
        {...rest}
      >
        {icon ? (
          <Box
            color={iconColor}
            as="span"
            width="24px"
            height="24px"
            mr="20px"
            display="block"
          >
            {icon ? React.cloneElement(icon, { size: "24px" }) : null}
          </Box>
        ) : null}
        <Text
          variant="sm"
          fontSize={[2, 3, 5]}
          fontFamily="heading"
          color="inherit"
          as="span"
        >
          {label}
        </Text>
      </NavigationLink>
    </React.Fragment>
  );
};

const HeaderNarrowStack = styled(Stack)`
  & > div {
    margin-left: 0;
  }
`;

interface NavigationItemProps extends BoxProps {
  icon?: JSX.Element;
  iconColor?: string;
  label: string;
  slug: string;
  isActive?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}

export const HeaderNarrow = ({
  menuItems,
  isWhiskymofoEnabled,
  transparentHeader = false,
}: {
  menuItems: storefrontNavigationItemProps[];
  isWhiskymofoEnabled: boolean;
  transparentHeader: boolean;
}): JSX.Element => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const initialOpen = useResponsiveValue([false, false, true]);

  useEffect(() => {
    setIsOpen(initialOpen);
  }, [initialOpen]);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    hideChatLogo(!isOpen);
  };

  const goTo = (slug: string) => navigate(slug);

  const modifiedMenuItems = menuItems.map((item) => ({
    ...item,
    label: item.labelMobile || item.label,
  }));

  const menuItemWithContact =
    menuItems.length > 0
      ? [
          ...menuItems,
          {
            link: "/contact",
            label: "Contact Us",
            audience: MARKET_AUDIENCE.ALL,
            device: [DEVICE.MOBILE, DEVICE.DESKTOP],
          },
        ]
      : [];

  const hideChatLogo = (hide: boolean) => {
    const chatLogo = document.getElementById(
      "hubspot-messages-iframe-container"
    );

    if (isBrowser() && chatLogo) {
      if (window.innerWidth < 768) {
        hide
          ? chatLogo.style.setProperty("z-index", "-1", "important")
          : chatLogo.style.removeProperty("z-index");
      }
    }
  };

  return (
    <Section
      py={12}
      backgroundColor={transparentHeader ? "transparent" : "secondary"}
      borderBottom={
        transparentHeader ? ["1px solid rgba(255, 255, 255, .25)"] : 0
      }
    >
      <HeaderNarrowStack space={2}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexBasis="100%"
        >
          <Box
            display="flex"
            flexGrow={3}
            flexShrink={1}
            flexBasis="0"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Stack space={3} horizontal>
              <Box
                display={["flex", "flex", "none"]}
                minHeight="36px"
                width="22px"
                flexWrap="wrap"
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexShrink={0}
                  width={
                    !(
                      isWhiskymofoSignupPage(location?.pathname) ||
                      isWhiskymofoDealsPage(location?.pathname)
                    )
                      ? `22px`
                      : `auto`
                  }
                  cursor="pointer"
                  color="white"
                  zIndex="15"
                  onClick={toggleOpen}
                >
                  {!isWhiskymofoSignupPage(location?.pathname) &&
                    !isWhiskymofoDealsPage(location?.pathname) && (
                      <HamburgerToggle isOpen={isOpen} color="white" />
                    )}

                  {WHISKYMOFO_ENABLED ? (
                    (isWhiskymofoSignupPage(location?.pathname) ||
                      isWhiskymofoDealsPage(location?.pathname)) && (
                      <Box pt="4px">
                        <SiteToggle value={isWhiskymofoEnabled} />
                      </Box>
                    )
                  ) : (
                    <HamburgerToggle isOpen={isOpen} color="white" />
                  )}
                </Box>
                <Box>
                  <DropDownOverlay open={isOpen} onClick={toggleOpen} />
                  <Box
                    position="absolute"
                    width="100%"
                    left="0"
                    top="56px"
                    zIndex="14"
                  >
                    <Collapse isOpen={isOpen} onClick={toggleOpen}>
                      <CollapsePanel p="0" position={"relative"}>
                        <Box
                          style={{ top: -36, right: 20 }}
                          position={"absolute"}
                          zIndex={1}
                        >
                          {WHISKYMOFO_ENABLED && (
                            <SiteToggle value={isWhiskymofoEnabled} />
                          )}
                        </Box>
                        <Card
                          alignItems="flex-start"
                          height="max-content"
                          borderRadius="0"
                          boxShadow="none"
                        >
                          {menuItemWithContact.map((item) => {
                            const { link, label } = item;
                            const isActive = location.pathname.endsWith("/")
                              ? location.pathname === `${link}/`
                              : location.pathname === link;

                            return (
                              <NavigationItem
                                key={link}
                                label={label}
                                // icon={icon}
                                slug={link}
                                isActive={isActive}
                                onClick={() => {
                                  setIsOpen(initialOpen);
                                  goTo(link);
                                }}
                              />
                            );
                          })}
                        </Card>
                      </CollapsePanel>
                    </Collapse>
                  </Box>
                </Box>
              </Box>
              {!isWhiskymofoSignupPage(location?.pathname) &&
                !isWhiskymofoDealsPage(location?.pathname) && <HeaderAuth />}
              {/* <IconLink href={phoneNumber} color="white">
                <PhoneIcon size="25px" />
              </IconLink> */}
            </Stack>
          </Box>
          <Box
            flexGrow={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <HeaderLogo isWhiskymofoEnabled={isWhiskymofoEnabled} />
          </Box>
          <Box
            flexGrow={3}
            flexShrink={1}
            flexBasis={0}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            {!isWhiskymofoSignupPage(location?.pathname) ? (
              <Stack
                space={isWhiskymofoDealsPage(location?.pathname) ? 1 : 3}
                horizontal
                alignItems="center"
              >
                {isWhiskymofoDealsPage(location?.pathname) && <HeaderAuth />}
                <HeaderSearch />
                <CartDropdown />
              </Stack>
            ) : (
              <LoginSignup />
            )}
          </Box>
        </Box>
        {menuItems.length > 0 && (
          <HeaderCategoryNav
            fontSize={[0, 1]}
            menuItems={modifiedMenuItems.filter((item) =>
              item.device.includes("mobile")
            )}
          />
        )}
      </HeaderNarrowStack>
    </Section>
  );
};
